function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
function _objectWithoutProperties(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import regeneratorRuntime from "regenerator-runtime";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import React, { useMemo, useRef, useEffect, useCallback } from "react";
import CarinaForm from "../../FormLayout";
import isEqual from "lodash.isequal";
import { FormContextProvider } from "../FormContext/FormContext";
import { withValidations } from "./utils/withValidations";
import { SilkyEngine as silkyEngine } from "./SilkyEngine";
import { isFunction } from "../../../utils/is";
import { ifDevMessage } from "../../../utils/devMessage";
var formDefaultProps = {
    allowDefaultSubmit: false,
    initializationMode: 'static',
    readOnly: false,
    isRtl: false,
    validateOnInitialization: false
};
export function Form(_param) {
    var tmp = _param.initialValues, initialValuesProp = tmp === void 0 ? {} : tmp, readOnly = _param.readOnly, isRtl = _param.isRtl, onSubmit = _param.onSubmit, children = _param.children, initializationMode = _param.initializationMode, validateOnInitialization = _param.validateOnInitialization, allowDefaultSubmit = _param.allowDefaultSubmit, stack = _param.stack, propDrop = _objectWithoutProperties(_param, [
        "initialValues",
        "readOnly",
        "isRtl",
        "onSubmit",
        "children",
        "initializationMode",
        "validateOnInitialization",
        "allowDefaultSubmit",
        "stack"
    ]);
    var ref = useRef(silkyEngine(initialValuesProp)), formMethods = ref.current;
    var getInitialValues = formMethods.getInitialValues, setInitialValues = formMethods.setInitialValues, setValue = formMethods.setValue, getAllValues = formMethods.getAllValues, resetValues = formMethods.resetValues, resetErrors = formMethods.resetErrors, runAllValidations = formMethods.runAllValidations, getSubmitting = formMethods.getSubmitting, setSubmitting = formMethods.setSubmitting, getDirty = formMethods.getDirty, setDirty = formMethods.setDirty, determineDirty = formMethods.determineDirty;
    useMemo(function() {
        if (!isEqual(getInitialValues(), initialValuesProp)) {
            if (initializationMode === 'reinitialize') {
                setInitialValues(initialValuesProp);
                resetValues();
                resetErrors();
                if (validateOnInitialization) {
                    runAllValidations();
                }
            } else if (initializationMode === 'merge') {
                var previousInitialValues = getInitialValues();
                var currentValues = getAllValues();
                setInitialValues(initialValuesProp);
                for(var fieldName in _objectSpread({}, currentValues, initialValuesProp)){
                    if (!isEqual(previousInitialValues[fieldName], initialValuesProp[fieldName]) && isEqual(previousInitialValues[fieldName], currentValues[fieldName])) {
                        setValue(fieldName, initialValuesProp[fieldName]);
                    }
                }
            } else if (initializationMode === 'static') {
                console.error('[Form] initialValues prop changed in static mode. initialValues will not be updated in this mode.');
            }
        }
    }, [
        initialValuesProp,
        initializationMode
    ]);
    useEffect(function() {
        if (validateOnInitialization) {
            runAllValidations();
        }
    }, []);
    var formContext = useMemo(function() {
        return _objectSpread({
            readOnly: readOnly,
            isRtl: isRtl
        }, formMethods);
    }, [
        formMethods,
        isRtl,
        readOnly
    ]);
    var handleSubmit = useCallback(function() {
        var _ref = _asyncToGenerator(regeneratorRuntime.mark(function _callee(event) {
            var allValidationErrors, onSubmitData, errorMessage;
            return regeneratorRuntime.wrap(function _callee$(_ctx) {
                while(1)switch(_ctx.prev = _ctx.next){
                    case 0:
                        event.persist();
                        if (!allowDefaultSubmit) {
                            event.preventDefault();
                        }
                        setSubmitting(true);
                        _ctx.next = 5;
                        return runAllValidations();
                    case 5:
                        allValidationErrors = _ctx.sent;
                        onSubmitData = {
                            values: getAllValues(),
                            initialValues: getInitialValues(),
                            errors: allValidationErrors,
                            hasErrors: Array.isArray(Object.keys(allValidationErrors)) && Object.keys(allValidationErrors).length > 0,
                            submitting: getSubmitting(),
                            dirty: getDirty(),
                            resetForm: function resetForm() {
                                resetValues();
                                resetErrors();
                                setDirty(determineDirty());
                            },
                            formContext: formContext
                        };
                        if (!isFunction(onSubmit)) {
                            _ctx.next = 19;
                            break;
                        }
                        _ctx.prev = 8;
                        _ctx.next = 11;
                        return withValidations(onSubmit)(event, onSubmitData);
                    case 11:
                        _ctx.next = 19;
                        break;
                    case 13:
                        _ctx.prev = 13;
                        _ctx.t0 = _ctx["catch"](8);
                        errorMessage = '';
                        if (typeof _ctx.t0 === 'string') {
                            errorMessage = _ctx.t0;
                        } else if (_instanceof(_ctx.t0, Error)) {
                            errorMessage = _ctx.t0.toString();
                        }
                        ifDevMessage(errorMessage);
                        throw Error("[Form] Uncaught submission error ".concat(errorMessage));
                    case 19:
                        setSubmitting(false);
                    case 20:
                    case "end":
                        return _ctx.stop();
                }
            }, _callee, null, [
                [
                    8,
                    13
                ]
            ]);
        }));
        return function(event) {
            return _ref.apply(this, arguments);
        };
    }(), [
        allowDefaultSubmit,
        setSubmitting,
        runAllValidations,
        getAllValues,
        getInitialValues,
        getSubmitting,
        getDirty,
        formContext,
        onSubmit,
        resetValues,
        resetErrors,
        setDirty,
        determineDirty
    ]);
    return _jsx(FormContextProvider, {
        value: formContext,
        children: _jsx(CarinaForm, _objectSpread({}, propDrop, {
            onSubmit: handleSubmit,
            stack: stack !== null && stack !== void 0 ? stack : true,
            noValidate: true,
            children: typeof children === 'function' ? children(formContext) : children
        }))
    });
}
Form.defaultProps = formDefaultProps;
Form.Section = CarinaForm.Section;
